import {
  forwardRef,
  useContext,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react"
import { createPortal } from "react-dom"

import { Button } from "@coval/ui"
import { Plus } from "lucide-react"

import { FilterBarContext } from "@/contexts/FilterBarContext"
import { cn } from "@/utils/cn"

export function FilterBarScope({ children, ...props }: ComponentProps<"div">) {
  const [target, setTarget] = useState<HTMLDivElement | null>(null)
  return (
    <FilterBarContext.Provider value={target}>
      <div {...props}>
        {children}
        <div ref={setTarget} className="mt-2 w-full empty:hidden" />
      </div>
    </FilterBarContext.Provider>
  )
}

/**
 * The control that opens the filter menu from the bar itself. It is handed to
 * `ActiveFilterBar` already wired to a menu so the menu can open against this
 * button rather than against the toolbar's funnel, which sits in another row.
 */
export const AddFilterButton = forwardRef<
  HTMLButtonElement,
  ComponentProps<typeof Button>
>((props, ref) => (
  <Button
    ref={ref}
    type="button"
    icon
    variant="ghost"
    className="size-6"
    tooltip="Add filter"
    {...props}
  >
    <Plus />
  </Button>
))

AddFilterButton.displayName = "AddFilterButton"

export function ActiveFilterBar({
  children,
  onClear,
  addControl,
  bleed = true,
}: {
  children: ReactNode
  onClear: () => void
  /** Opens the filter menu beside the chips; see `AddFilterButton`. */
  addControl?: ReactNode
  /**
   * The band meets the platter's edges by bleeding back through the gutter its
   * host pads — `px-6` across and `pb-3` below the box the band is laid out in.
   * A host that does not pad that way passes `false` and gets a band that keeps
   * to the box it was given rather than painting over its neighbours.
   */
  bleed?: boolean
}) {
  const target = useContext(FilterBarContext)
  const bar = (
    <div
      role="region"
      aria-label="Active filters"
      // A band in the navigation's own contrast surface, opened by a rule
      // across the full width, separates the chips from the controls above
      // them. Where the host pads the band's box, the band bleeds back through
      // that gutter so it meets the platter's edges and the table beneath it,
      // then repays the padding inside itself to sit the chips on the
      // toolbar's own left edge. Without that gutter it spans its own box.
      className={cn(
        "flex min-w-0 items-start gap-3 border-t border-coval-border bg-surface-contrast py-3",
        bleed
          ? "-mx-6 -mb-3 w-[calc(100%+3rem)] basis-[calc(100%+3rem)] px-6"
          : "w-full basis-full",
      )}
    >
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        {children}
        {addControl}
      </div>
      <Button
        variant="ghost"
        className="h-6 shrink-0 px-2"
        aria-label="Clear all filters"
        onClick={onClear}
      >
        Clear
      </Button>
    </div>
  )
  return target === undefined ? bar : target ? createPortal(bar, target) : null
}
